// zod schema
import { custom, z } from 'zod';

// 课程元素类型
export enum ElementType {
    INTERACTIVE = "interactive",
    TIMELINE = "timeline",
    DRAG_AND_DROP = "drag_and_drop",
    WORD_SEARCH = "word_search",
    FLASH_CARD = "flashcard",
    LESSON_SUMMARY = "lesson_summary",
    RECOMMEND_VIDEOS = "recommend_videos",
    RECOMMEND_SHORTS = "recommend_shorts",
    REVIEW_VIDEOS = "review_videos",
    SPELLING_DICTATION = "spelling_dictation",
    QUESTION = "question",
    QUESTION_V2 = "question_v2",
    HIGHLIGHT = "highlight",
    GRAMMAR_FILL_IN_BLANK = "grammar_fill_in_blank",
    GRAMMAR_DRAG_AND_DROP = "grammar_drag_and_drop",
    KEY_PROPERTY = "key_property",
    EXAMPLE_QUESTION = "example_question",
    SPELLING_DICTATION_V2 = "spelling_dictation_v2",
    TIMELINE_SPARK = "timeline_spark",
}

export const InstructorProfileSchema = z.object({
  name: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  full_body_url: z.string().nullable().optional(),
  greeting: z.string().nullable().optional()
});

export type RawInstructorProfile = z.infer<typeof InstructorProfileSchema>;

export const TabConfigSchema = z.object({
  showPlay: z.boolean().optional().default(false),
  showLearn: z.boolean().optional().default(true),
  showPractice: z.boolean().optional().default(true),
  showChallenge: z.boolean().optional().default(false),
  showChapterTest: z.boolean().optional().default(false),
  showProblemSet: z.boolean().optional().default(false),
});

export type TabConfig = z.infer<typeof TabConfigSchema>;

// 学校-年级映射 Schema
export const SchoolDistrictGradeMappingSchema = z.object({
  school_district: z.string(),
  grades: z.array(z.string()).optional()  // 可选字段，支持只配学校不配年级的场景
});

export type SchoolDistrictGradeMapping = z.infer<typeof SchoolDistrictGradeMappingSchema>;

export const QuestSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  title: z.string(),
  book_name: z.string().nullable().optional(),
  primary_color_theme: z.string(),
  secondary_color_theme: z.string(),
  description: z.string(),
  instructor_profile: InstructorProfileSchema,
  // 可为空
  cover_image: z.string().nullable().optional(),
  lock: z.boolean(),
  subject: z.string().nullable().optional(),
  series: z.string().nullable().optional(),
  order: z.number(),
  visible: z.boolean(),
  tab_config: TabConfigSchema.nullable().optional(),
  publisher: z.string().nullable().optional(),
  // 学区-年级配置字段
  default_grade: z.string().nullable().optional(),  // 默认年级，如 "Grade 3"
  public_school_districts: z.array(z.string()).nullable().optional(),  // 公立学区列表
  school_district_grade_mapping: z.array(SchoolDistrictGradeMappingSchema).nullable().optional(),  // 特殊映射（补充意外情况）
});

export type RawQuest = z.infer<typeof QuestSchema>;

export const ChapterSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  order: z.number(),
  quest_id: z.string(),
  visible: z.boolean(),
  unit: z.string().nullable().optional(),
  questions: z.array(z.any()).nullable().optional(),
});

export type RawChapter = z.infer<typeof ChapterSchema>;

export const ChapterBaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number(),
});

export type ChapterBase = z.infer<typeof ChapterBaseSchema>;

export const LessonSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  title: z.string(),
  keypoints: z.array(z.string()).nullable().optional(),
  description: z.string().nullable().optional(),
  order: z.number(),
  chapter_id: z.string(),
  welcome_message: z.string().nullable().optional(),
  lesson_prefix: z.string().nullable().optional(),
  questions: z.array(z.any()).nullable().optional(),
  challenge: z.array(z.any()).nullable().optional(),
  textbook_exercises: z.array(z.any()).nullable().optional(),
  play_elements: z.array(z.any()).nullable().optional(),
  type: z.enum(['static', 'dynamic']).nullable().optional().default('static'),
  elements: z.array(z.any()).nullable().optional(), // For dynamic lessons, stores elements directly
  skill_list: z.array(z.string()).nullable().optional(), // For dynamic lessons, list of skill IDs to fetch from syllabus_skill
});

export type RawLesson = z.infer<typeof LessonSchema>;

export const ElementSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  title: z.string(),
  order: z.number(),
  lesson_id: z.string(),
  // element_type只会有ElementType的值
  element_type: z.nativeEnum(ElementType),
  url: z.string().nullable().optional(),
  exp: z.number().nullable().optional(),
  chip: z.string().nullable().optional(),
  metadata: z.any().nullable().optional(),
  youtube_meta: z.any().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  questions: z.union([z.array(z.any()), z.any()]).nullable().optional(),
  question_id: z.string().nullable().optional(),
  short_videos: z.array(z.any()).nullable().optional(),
  key_points: z.array(z.any()).nullable().optional(),
  timeline_events: z.array(z.any()).nullable().optional(),
  suggested_questions: z.array(z.any()).nullable().optional(),
  message_to_introduce: z.string().nullable().optional(),
  flashcard: z.array(z.any()).nullable().optional(),
  recommend_videos: z.array(z.any()).nullable().optional(),
  recommend_shorts: z.array(z.any()).nullable().optional(),
  review_videos: z.array(z.any()).nullable().optional(),
  lesson_summary: z.array(z.any()).nullable().optional(),
  spelling_dictation: z.array(z.any()).nullable().optional(),
  more_detail: z.string().nullable().optional(),
  key_property: z.any().nullable().optional(),
  timeline_spark: z.array(z.any()).nullable().optional(),
});

export type RawElement = z.infer<typeof ElementSchema>;

export enum ModeType {
    CHAT = "chat",
    LEARN = "learn",
    PLAY = "play",
    PRACTICE = "practice",
    CHALLENGE = "challenge",
    TEST = "test",
    PROBLEM_SET = "problemSet",
    MISTAKE = "mistake",
}

export enum QuestionType {
  DICTATION = "dictation",
  MULTIPLE_CHOICE = "multiple_choice",
  BLANK_FILLING = "blank_filling",
  MATH_BLANK_FILLING = "math_blank_filling",
  HIGHLIGHT = "highlight",
  GRAMMAR_CLASSIFICATION = "grammar_classification",
  GRAMMAR_DRAG_AND_DROP = "grammar_drag_and_drop",
  GRAMMAR_FILL_IN_BLANK = "grammar_fill_in_blank",
}

export interface Question {
  id: string;
  type: QuestionType;
  question: string;
  answer: string;
  options?: string[];
  explanation: string;
  question_type: QuestionType;
  review_status: 'pending' | 'pass' | 'reject';
  metadata?: Record<string, unknown> | null;
  picture?: string | null;
}

export interface PracticeQuestion extends Question {
  wasWrong: boolean;
}

export interface InstructorProfile extends RawInstructorProfile {
    id: string,
}

export interface Element extends RawElement {
}

export interface Lesson extends RawLesson {
    elements: Element[]
}

export interface Chapter extends RawChapter {
    lesson_list: Lesson[],
}

// 课程结构 quest - chapter - lesson - element

export interface Quest extends RawQuest {
    chapters: Chapter[],
}

export type RawQuestsData = {
  quests: Quest[],
  chapters: Chapter[],
  lessons: Lesson[],
  elements: Element[]
}

export interface ElementProgress {
    element_id: string,
    progress: any
}

export interface ElementMapValue extends RawElement {
    finished: boolean;
    data?: any;
}

export interface History {
    chapter: Chapter,
    // element_map: Record<string, ElementMapValue>,
    status: 'new' | 'in-progress' | 'completed'
}

export interface AnswerRecord {
  questionId: string;
  selectedOption: string;
  isCorrect: boolean;
  timestamp: number;
  firstSelectedOption?: string;
  firstIsCorrect?: boolean;
}

export const ExtracurricularSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  playlist_title: z.string(),
  cover: z.string(),
  video_url_list: z.array(z.string()),
  about: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  grade: z.string().nullable().optional(),
  videoCount: z.number().optional(),
  quizCount: z.number().optional(),
});

export type Extracurricular = z.infer<typeof ExtracurricularSchema>;

export const PetAssetsSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  url: z.string(),
  lock: z.boolean(),
  // 枚举值：background, face, head, top, hand, ground, back, minipet, down, suitecase
  part: z.enum(['background', 'face', 'head', 'top', 'hand', 'ground', 'back', 'minipet', 'adventureBg', 'down', 'suitecase']),
  shop_thumbnail: z.string().nullable().optional(),
  price: z.number(),
  smile: z.string().nullable().optional(),
  hello: z.string().nullable().optional(),
  clap: z.string().nullable().optional(),
  think: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  type: z.enum(['normal', 'bff', 'christmas']),
  sound: z.string().nullable().optional(),
});

export type PetAssets = z.infer<typeof PetAssetsSchema>;

export const NotificationTypeEnum = z.enum(['invite', 'reply', 'system', 'force']);

export type NotificationType = z.infer<typeof NotificationTypeEnum>;

export const NotificationSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  content: z.string(),
  title: z.string(),
  has_read: z.boolean(),
  type: NotificationTypeEnum,
  user_id: z.string(),
  reply: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  event: z.string().nullable().optional(),
});

export type RawNotification = z.infer<typeof NotificationSchema>;


export const ProfileSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  email: z.string().nullable().optional(),
  nickname: z.string().nullable().optional(),
  birth_year: z.number().nullable().optional(),
  birth_month: z.number().nullable().optional(),
  zip_code: z.string().nullable().optional(),
  profile_photo: z.string().nullable().optional(),
  is_onboarding_finished: z.boolean().nullable().optional(),
  onboard_data: z.any().nullable().optional(),
  stats: z.any().nullable().optional(),
  reward: z.object({
    heart: z.number().nullable().optional(),
    diamond: z.number().nullable().optional(),
    total_heart: z.number().nullable().optional(),
    game_time_remaining: z.number().nullable().optional(),
    last_heart_recovery: z.number().nullable().optional(),
  }).nullable().optional(),
  role: z.enum(['parent', 'parent_v2', 'student', 'teacher', 'developer']).nullable().optional(),
  state: z.any().nullable().optional(),
  grade: z.string().nullable().optional(),
  quest_tutorial: z.boolean(),
  learn_tutorial: z.boolean(),
  has_password: z.boolean(),
  account_type: z.string().nullable().optional(),
  accept_spaceship: z.boolean(),
  accept_landing_time: z.string().nullable().optional(),
  pet_onboard_data: z.object({
    name: z.string().nullable().optional(),
    hobbies: z.string().nullable().optional(),
    version: z.string().nullable().optional(),
    onboard_date: z.string().nullable().optional(),
    favorite_color: z.string().nullable().optional(),
    ready_to_start: z.string().nullable().optional(),
    completion_date: z.string().nullable().optional(),
    has_sent_first_message: z.boolean().nullable().optional(),
    has_messaged_after_gift: z.boolean().nullable().optional(),
  }).nullable().optional(),
  receive_gems: z.boolean(),
  current_outfit: z.object({
    top: z.number().nullable().optional(),
    back: z.number().nullable().optional(),
    hand: z.number().nullable().optional(),
    ground: z.number().nullable().optional(),
    head: z.number().nullable().optional(),
    face: z.number().nullable().optional(),
    down: z.number().nullable().optional(),
    suitecase: z.number().nullable().optional(),
  }).nullable().optional(),
  current_bg: z.number().nullable().optional(),
  cabinet: z.object({
    face: z.array(z.number()).nullable().optional(),
    top: z.array(z.number()).nullable().optional(),
    back: z.array(z.number()).nullable().optional(),
    hand: z.array(z.number()).nullable().optional(),
    ground: z.array(z.number()).nullable().optional(),
    head: z.array(z.number()).nullable().optional(),
    down: z.array(z.number()).nullable().optional(),
    suitecase: z.array(z.number()).nullable().optional(),
  }).nullable().optional(),
  current_minipet: z.number().nullable().optional(),
  minipets: z.object({ minipet: z.array(z.number()).nullable().optional(), custom: z.array(z.number()).nullable().optional() }).nullable().optional(),
  game_tutorial: z.boolean(),
  subscribe_report: z.boolean(),
  special_settings: z.any().nullable().optional(),
  my_cards: z.array(z.number()).nullable().optional(),
  adventure_id: z.number().nullable().optional(),
  voice_id: z.string().nullable().optional(),
  // timestampz
  last_adventure: z.string().nullable().optional(),
  travel_history: z.array(z.any()).nullable().optional(),
  adventure_bg: z.array(z.number()).nullable().optional(),
  current_stage: z.enum(['stage1', 'stage2', 'stage3', 'stage4']).nullable().optional(),
  pet_config: z.any().nullable().optional(),
  pet_welcome_showed: z.boolean().nullable().optional(),
  free_subject: z.string().nullable().optional(),
  subject_changed_at: z.string().nullable().optional(),
  email_verified_at: z.string().nullable().optional(),
});

export type Profile = z.infer<typeof ProfileSchema>;

export const AdventureSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  user_id: z.string(),
  status: z.enum(['idle', 'unstarted', 'ready', 'in_progress', 'completed']).nullable().optional(),
  return_time: z.string().nullable().optional(),
  reward_id: z.number().nullable().optional(),
  gain: z.boolean().nullable().optional(),
});

export type Adventure = z.infer<typeof AdventureSchema>;


export const PetCardSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  image: z.string(),
  level: z.enum(['R', 'SR', 'SSR']),
  series: z.enum(['Art Series', 'Adventure Series', 'Comic Series']),
  lock: z.boolean(),
  destination: z.string().nullable().optional(),
  background: z.string().nullable().optional(),
  badge: z.string().nullable().optional(),
  postcard: z.array(z.any()).nullable().optional(),
  nation: z.string().nullable().optional(),
  asset_id: z.number().nullable().optional(),
  stage: z.optional(z.enum(['stage1', 'stage2', 'stage3', 'stage4'])),
  animate_id: z.number().nullable().optional(),
});

export type PetCard = z.infer<typeof PetCardSchema>;

export interface DiagnosisQuestion {
  id: string;
  question_data: Question;
  chapter_title: string;
  chapter_order: number;
  lesson_title: string;
  lesson_order: number;
  lesson_prefix?: string;
  question_index: number;
}

// Syllabus Skill Schema for math skill extraction
export const SyllabusSkillSchema = z.object({
  id: z.number(),
  element_id: z.string(),
  quest_id: z.string(),
  chapter_id: z.string(),
  lesson_id: z.string(),
  title: z.string().nullable().optional(),
  math_skill_order: z.string().nullable().optional(),
  elements_id: z.array(z.string()),
  elements_json: z.array(z.any()), // JSONB array of elements
  question_ids: z.array(z.string()).nullable().optional(), // Practice question IDs
  challenge_ids: z.array(z.string()).nullable().optional(), // Challenge question IDs
  diagnostic_question_ids: z.array(z.string()).nullable().optional(), // Diagnostic representative question IDs (2-3, prioritized)
  created_at: z.string(),
  updated_at: z.string(),
});

export type SyllabusSkill = z.infer<typeof SyllabusSkillSchema>;

// Extended type with parsed elements for easier use
export interface SyllabusSkillWithElements extends Omit<SyllabusSkill, 'elements_json'> {
  elements_json: Element[]; // Parsed elements
  type: string;
}

// Skill type enum to distinguish skill origin/status
export type SkillType = 'matched' | 'proposed' | 'proposed_accepted' | 'revised' | 'revised_accepted';

// YAML Data Structure Types for AoPS Prealgebra
export interface YAMLSkill {
  source_book: string;
  element_id: string;        // Used to query syllabus_skill table
  title: string;
  optimized_title: string;  // Optimized title for better understanding
  skill_order: string;  // e.g., "1.2.3"
  match_quality: 'strong' | 'good' | 'limited';  // Quality of match for matched skills
  match_reason: string;
  optimization_notes: string;  // Additional notes about optimization
  content_outside_scope: string; // Notes on content outside the scope
  picture?: string;          // Image URL
  picture_position?: string; // Image position (left/right/bottom)
  copyright_image_url?: string;  // Copyright source URL
}

export interface YAMLTargetLesson {
  lesson_id: string;
  lesson_title: string;
  lesson_order: number;
  key_concepts: string[];
  elements?: any[]; // Additional elements from syllabus_lesson table
}

/**
 * Proposed skill structure (used in both YAML and runtime)
 * Represents skills that fill missing gaps in the curriculum
 */
export interface ProposedNewSkill {
  for_missing_content: string;
  skillType: 'proposed' | 'proposed_accepted';  // Required field
  title: string;
  proposed_skill_order: number;
  skill_content: string;
  examples: string;
  explanation: string;
  visual?: {
    required?: boolean;
    mode?: 'coordinate_plane' | 'geometry' | 'number_line' | 'none';
    description?: string;
    payload?: unknown;
  };
  picture?: string;
  picture_position?: string;
  copyright_image_url?: string;  // Copyright source URL
}

/**
 * Revised skill structure (used in both YAML and runtime)
 * Represents modified versions of existing skills
 */
export interface RevisedSkill {
  element_id: string;           // Reference to original skill
  skillType: 'revised' | 'revised_accepted';  // Required field
  revised_title: string;
  source_book: string;
  skill_order: string;
  skill_content: string;
  examples: string;
  explanation: string;
  visual?: {
    required?: boolean;
    mode?: 'coordinate_plane' | 'geometry' | 'number_line' | 'none';
    description?: string;
    payload?: unknown;
  };
  picture?: string;
  picture_position?: string;
  copyright_image_url?: string;  // Copyright source URL
}

export interface YAMLLesson {
  target_lesson: YAMLTargetLesson;
  matched_skills: YAMLSkill[];
  removed_skills?: RemovedSkill[];
  missing_from_sources?: string[];
  combined_coverage?: 'good' | 'limited' | 'poor';
  combined_coverage_assessment?: string;
  proposed_new_skills?: ProposedNewSkill[];
  revised_skills?: RevisedSkill[];
}

export interface DuplicatedSkill {
  element_id: string;
  skill_order: string;
  source_book: string;
  title: string;
  used_in_lessons: string[];
}

export interface RemovedSkill {
  source_book: string;
  element_id: string;
  title: string;
  optimized_title?: string;
  skill_order: string;
  removal_reason: string;
  match_quality?: string;
  match_reason?: string;
  optimization_notes?: string;
  content_outside_scope?: string;
}

export interface ChapterOptimizationMetadata {
  duplicated_skills_across_lessons?: DuplicatedSkill[];
}

export interface YAMLChapter {
  target_chapter_id: string;
  target_chapter_title: string;
  target_chapter_order: number;
  lessons: YAMLLesson[];
  chapter_optimization_metadata?: ChapterOptimizationMetadata;
}

export interface YAMLMatchingSummary {
  target_book_info: {
    title: string;
    source_file: string;
    total_chapters: number;
    processed_chapters?: number;
    chapter_range?: string;
    total_lessons: number;
  };
  source_books_info: {
    collection_name?: string;
    books?: string[];
    source_files: string[];
    total_source_chapters?: number;
    total_source_skills: number;
  };
  matching_statistics?: {
    total_target_chapters: number;
    total_target_lessons: number;
    lessons_with_strong_matches: number;
    lessons_with_good_matches: number;
    lessons_with_limited_matches: number;
    lessons_with_no_matches: number;
  };
  timestamp?: string;
}

export interface YAMLData {
  matching_summary: YAMLMatchingSummary;
  [chapterKey: string]: YAMLChapter | YAMLMatchingSummary; // chapter_X_name keys
}

// Enhanced types with database data
/**
 * EnrichedSkill - Unified skill type for runtime use
 * - For matched skills: extends YAMLSkill with syllabusSkillData
 * - For proposed skills: extends ProposedNewSkill (flat structure, no nesting)
 * - For revised skills: handled separately in revised_skills array
 */
export interface EnrichedSkill extends YAMLSkill {
  syllabusSkillData?: SyllabusSkillWithElements;  // Only for matched skills
  skillType: SkillType;  // Type of skill
  // For proposed skills, the following fields from ProposedNewSkill are merged at top level:
  for_missing_content?: string;
  proposed_skill_order?: number;
  skill_content?: string;
  examples?: string;
  explanation?: string;
  visual?: {
    required?: boolean;
    mode?: 'coordinate_plane' | 'geometry' | 'number_line' | 'none';
    description?: string;
    payload?: unknown;
  };
}

export interface EnrichedLesson extends Omit<YAMLLesson, 'matched_skills'> {
  matched_skills: EnrichedSkill[];
}

export interface EnrichedChapter extends Omit<YAMLChapter, 'lessons'> {
  lessons: EnrichedLesson[];
}
